import React from 'react'
import styled from '@emotion/styled/macro'

const SampleSection = styled.div`
  padding: 0 16px 16px;
`

const SampleTitle = styled.h3`
  font-size: 14px;
  font-weight: var(--bold-weight);
  margin: 0 0 var(--gutter-small) 0;
  color: var(--black);
`

const SampleGrid = styled.fieldset`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 4px 0;
  border: 0;
  margin: 0;

  legend {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 1px, 1px);
    white-space: nowrap;
    border: 0;
  }

  @media (max-width: 768px) {
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`

const ScrollContainer = styled.div`
  position: relative;

  @media (max-width: 768px) {
    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      width: 24px;
      pointer-events: none;
      z-index: 1;
    }

    &::before {
      left: 0;
      background: linear-gradient(
        to right,
        var(--white) 0%,
        rgba(255, 255, 255, 0) 100%
      );
      opacity: 0;
      transition: opacity 0.2s ease;
    }

    &::after {
      right: 0;
      background: linear-gradient(
        to left,
        var(--white) 0%,
        rgba(255, 255, 255, 0) 100%
      );
      opacity: 0;
      transition: opacity 0.2s ease;
    }

    &[data-fade-left='true']::before {
      opacity: 1;
    }

    &[data-fade-right='true']::after {
      opacity: 1;
    }
  }
`

// removed list-row specific label/prompt elements in favor of pill buttons

const SampleChipButton = styled.button`
  padding: 4px 8px;
  background: var(--white);
  border: 1px solid var(--gray-300);
  border-radius: 9999px;
  font-size: 12px;
  font-family: var(--default-family);
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
  color: var(--black);
  white-space: nowrap;
  line-height: 1.3;
  flex: 0 0 auto;

  &:hover {
    background: var(--gray-50);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--primary-color-transparent, rgba(0, 0, 0, 0.1));
    border-color: var(--primary-color);
  }

  &:active {
    background: var(--gray-100);
  }
`

export const samplePrompts = [
  {
    label: '彼氏へ贈る...',
    prompt:
      '20代前半の大学生の彼氏に贈る誕生日プレゼントです。カジュアルで大人っぽい配色にしてください。'
  },
  {
    label: 'ビジネス男性...',
    prompt:
      '30代男性がビジネスシーンで使える、シックで落ち着いた配色にしてください。'
  },
  {
    label: 'ご褒美に...',
    prompt:
      '30代女性です。いつも頑張っている自分へのご褒美に、気分が上がるフェミニンな配色にしてください。'
  },
  {
    label: '母へ感謝...',
    prompt:
      '50代女性である母に贈ります。落ち着いた中にも華やかさがあるカラーにしてください。'
  },
  {
    label: '推しカラー赤...',
    prompt:
      '20代女性の推し活用です。推しのイメージカラーが赤なので、情熱的でかわいらしい赤を主役にしてください。'
  },
  {
    label: '遠征ライブ用...',
    prompt:
      'ライブ遠征用です。元気が出るような明るいカラーに、推しカラーをアクセントにしてください。'
  },
  {
    label: '秋の旅行用...',
    prompt:
      '秋の旅行に持っていきたくなるような、あたたかみがあって落ち着いた配色にしてください。'
  },
  {
    label: '海好き彼氏...',
    prompt:
      '20代後半の男性に贈ります。海を連想させるようなブルーを基調にした爽やかなデザインにしてください。'
  },
  {
    label: 'おそろい用...',
    prompt:
      '20代のカップルで使いたいお揃いアイテムです。男性にも女性にも合うバランスの取れた配色にしてください。'
  },
  {
    label: '記念日彼女...',
    prompt:
      '20代女性との記念日に贈る財布です。落ち着いた中にも特別感のあるカラーにしてください。'
  },
  {
    label: 'ナチュラル系...',
    prompt:
      '30代女性が持つナチュラル志向の財布です。ベージュやグレージュなど、優しい自然な色合いにしてください。'
  },
  {
    label: 'モノトーン系...',
    prompt:
      '30代男性が使うモノトーンスタイルの財布です。白・黒・グレーをベースに洗練された印象にしてください。'
  }
]

interface Props {
  onSampleClick: (prompt: string) => void
}

const SamplePromptsSectionComponent: React.FC<Props> = ({ onSampleClick }) => {
  const gridRef = React.useRef<HTMLFieldSetElement | null>(null)
  const containerRef = React.useRef<HTMLDivElement | null>(null)

  React.useEffect(() => {
    const el = gridRef.current
    const container = containerRef.current
    if (!el || !container) {
      return
    }

    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el
      const canScroll = scrollWidth > clientWidth + 1
      const atStart = scrollLeft <= 0
      const atEnd = scrollLeft + clientWidth >= scrollWidth - 1

      container.dataset.fadeLeft = canScroll && !atStart ? 'true' : 'false'
      container.dataset.fadeRight = canScroll && !atEnd ? 'true' : 'false'
    }

    update()
    el.addEventListener('scroll', update, {
      passive: true
    } as unknown as AddEventListenerOptions)
    window.addEventListener('resize', update)
    return () => {
      el.removeEventListener('scroll', update as EventListener)
      window.removeEventListener('resize', update)
    }
  }, [])

  return (
    <SampleSection>
      <ScrollContainer ref={containerRef}>
        <SampleGrid ref={gridRef}>
          <legend>おすすめのテーマ</legend>
          {samplePrompts.map((sample) => (
            <SampleChipButton
              key={sample.label}
              onClick={() => onSampleClick(sample.prompt)}
              aria-label={`テーマ: ${sample.label}`}
              title={sample.prompt}
            >
              {sample.label}
            </SampleChipButton>
          ))}
        </SampleGrid>
      </ScrollContainer>
    </SampleSection>
  )
}

export const SamplePromptsSection = React.memo(SamplePromptsSectionComponent)
